import { useEffect, useRef } from "react";

/** Touch zone width along the screen edge that starts the gesture. */
const EDGE_PX = 28;
/** Horizontal travel (toward screen center) that commits the open. */
const OPEN_PX = 36;

/**
 * Window-level "drag a finger in from the screen edge" gesture (mobile).
 * Fires `onOpen` once when a touch that started within EDGE_PX of the given
 * edge travels OPEN_PX toward the center without turning into a vertical
 * scroll. Listeners are passive — the open animates from the drawer itself
 * rather than tracking the finger 1:1.
 */
export function useEdgeSwipe(edge: "left" | "right", onOpen: () => void, enabled: boolean): void {
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;

  useEffect(() => {
    if (!enabled) return;
    let start: { x: number; y: number; id: number } | null = null;

    function onStart(e: TouchEvent) {
      if (e.touches.length !== 1) { start = null; return; }
      const t = e.touches[0]!;
      const inZone = edge === "left" ? t.clientX <= EDGE_PX : t.clientX >= window.innerWidth - EDGE_PX;
      start = inZone ? { x: t.clientX, y: t.clientY, id: t.identifier } : null;
    }
    function onMove(e: TouchEvent) {
      if (!start) return;
      const t = Array.from(e.touches).find((tt) => tt.identifier === start!.id);
      if (!t) return;
      const dx = t.clientX - start.x;
      const dy = t.clientY - start.y;
      // Mostly-vertical movement = a scroll, not an edge swipe.
      if (Math.abs(dy) > 32 && Math.abs(dy) > Math.abs(dx)) { start = null; return; }
      const travel = edge === "left" ? dx : -dx;
      if (travel > OPEN_PX) { start = null; onOpenRef.current(); }
    }
    function onEnd() { start = null; }

    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onEnd, { passive: true });
    window.addEventListener("touchcancel", onEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
      window.removeEventListener("touchcancel", onEnd);
    };
  }, [edge, enabled]);
}
